import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth0, type User } from '@auth0/auth0-react';
import { useSessionStore } from '@/store/session.store';
import { useAccountStore } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import { authService } from '@/services/auth.service';
import { readersService } from '@/services/readers.service';
import { PageLoader } from './PageLoader';
import { MobileAppSplash } from './MobileAppSplash';
import { getMobilePlatform } from '@/lib/device';
import { hasContinuedOnWeb, setContinuedOnWeb } from '@/lib/mobileGate';
import { env } from '@/lib/env';

/**
 * Best-effort extraction of `firstName`/`lastName` from the Auth0 user
 * profile. The server requires both to be non-empty strings (see
 * `AuthCallbackDto`), and we don't want to block first-time login on a
 * prompt for the user's name — the account rename flow lives on a
 * separate endpoint.
 *
 * Priority order:
 *   1. `given_name` / `family_name` — present for Google social logins.
 *   2. Split `name` on the first space — works for "Jane Doe" style.
 *   3. Fallback: email local-part as `firstName`, literal "User" as
 *      `lastName`. Guaranteed non-empty so DTO validation passes.
 */
function deriveNameFromAuth0Profile(user: User | undefined): {
  firstName: string;
  lastName: string;
} {
  const given = user?.given_name?.trim();
  const family = user?.family_name?.trim();
  if (given && family) return { firstName: given, lastName: family };

  const full = (user?.name ?? user?.nickname ?? '').trim();
  if (full) {
    const [first, ...rest] = full.split(/\s+/);
    if (first && rest.length > 0) {
      return { firstName: first, lastName: rest.join(' ') };
    }
    if (first) return { firstName: first, lastName: given || family || 'User' };
  }

  const emailLocal = user?.email?.split('@')[0]?.trim();
  return {
    firstName: given || emailLocal || 'Reader',
    lastName: family || 'User',
  };
}

/**
 * ProtectedRoute — wraps every authenticated route.
 *
 * Steps executed on first render per session:
 * 1. Wait for Auth0 to finish loading its session.
 * 2. If unauthenticated, redirect to /login and preserve `from`.
 * 3. Once we have an access token, `POST /auth/callback` to find-or-create
 *    the account + cache it in AccountStore.
 * 4. Fetch the authenticated user's readers so the header + nav can render.
 * 5. Mark `accountBootstrapped` in SessionStore so reloads skip the round-trip.
 *
 * `guestAccessible` routes (the library + catalogue) let unauthenticated
 * visitors fall through to the page instead of bouncing to `/login`. The
 * bootstrap/onboarding/mobile gates below only run for authenticated users,
 * so guests skip them entirely and browse the public catalogue.
 */
export function ProtectedRoute({ guestAccessible = false }: { guestAccessible?: boolean }) {
  const { isLoading, isAuthenticated, user } = useAuth0();
  const accessToken = useSessionStore((s) => s.accessToken);
  const bootstrapped = useSessionStore((s) => s.accountBootstrapped);
  const setBootstrapped = useSessionStore((s) => s.setAccountBootstrapped);
  const account = useAccountStore((s) => s.account);
  const setAccount = useAccountStore((s) => s.setAccount);
  const readers = useReadersStore((s) => s.readers);
  const setReaders = useReadersStore((s) => s.setReaders);
  const location = useLocation();

  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  // Mobile "coming soon" interstitial dismissal — seeded from localStorage so
  // it survives reloads / route changes once the visitor taps "continue".
  const [webContinue, setWebContinue] = useState(() => hasContinuedOnWeb());

  /**
   * Single-flight latch for the bootstrap sequence.
   *
   * Why a ref instead of a cancelled-flag + cleanup pattern:
   *
   *   - React 18 StrictMode fires every effect twice in development
   *     (setup → cleanup → setup). A cleanup-based cancel would abort
   *     the first request before it completes, and the second run
   *     would be blocked by the latch — so nothing would ever happen
   *     in dev.
   *
   *   - The runaway loop the user hit came from `account` being in the
   *     dep array: `setAccount(resolved)` triggered a re-render, which
   *     re-ran the effect, which fired `POST /auth/callback` again, ad
   *     nauseam.
   *
   * A ref keeps the guard out of the render cycle (no state change →
   * no re-render), so flipping it can't retrigger the effect. We keep
   * `user` in a ref too so a new reference from the Auth0 SDK doesn't
   * need to appear in the dep array.
   */
  const hasBootstrappedRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (isLoading || !isAuthenticated || !accessToken) return;
    if (hasBootstrappedRef.current) return;
    if (bootstrapped && account) {
      hasBootstrappedRef.current = true;
      return;
    }

    hasBootstrappedRef.current = true;
    setIsBootstrapping(true);

    (async () => {
      try {
        const { firstName, lastName } = deriveNameFromAuth0Profile(userRef.current);
        const { account: resolved } = await authService.callback({
          firstName,
          lastName,
          // Forwarded only so the server can send the welcome email on first
          // signup; never persisted. Omitted automatically when undefined.
          email: userRef.current?.email,
        });
        setAccount(resolved);

        const readers = await readersService.list();
        setReaders(readers);

        setBootstrapped(true);
      } catch (err) {
        // Release the latch so the user can retry (e.g. after fixing
        // a server outage) without a full page reload.
        hasBootstrappedRef.current = false;
        const message = err instanceof Error ? err.message : 'Failed to load account';
        setBootstrapError(message);
      } finally {
        setIsBootstrapping(false);
      }
    })();
  }, [
    isLoading,
    isAuthenticated,
    accessToken,
    bootstrapped,
    account,
    setAccount,
    setReaders,
    setBootstrapped,
  ]);

  if (!env.isAuth0Configured) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-or-cream-50 px-8 text-center">
        <h1 className="font-display text-2xl text-or-slate-900">Auth0 isn't configured yet</h1>
        <p className="max-w-md text-[14px] text-or-slate-600">
          Set <code>VITE_AUTH0_DOMAIN</code>, <code>VITE_AUTH0_CLIENT_ID</code>, and{' '}
          <code>VITE_AUTH0_AUDIENCE</code> in <code>client/.env</code> so the client can talk to
          Auth0 and the API.
        </p>
      </div>
    );
  }

  if (isLoading) return <PageLoader label="Signing you in…" />;
  if (!isAuthenticated) {
    // Guest browsing: render the page without an account. The catalogue
    // reads it issues are public; account-based features inside gate
    // themselves via the AccountGateModal.
    if (guestAccessible) return <Outlet />;
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Wait for `ApiAuthBridge` to land the access token before rendering
  // any child page. Without this guard, persisted `account` + `readers`
  // from a prior session let ProtectedRoute fall through to `<Outlet />`
  // while `accessToken` is still null — causing child-page `useEffect`
  // fetches to fire without an Authorization header, hit 401, and trip
  // the session-expired handler on every refresh.
  if (!accessToken) return <PageLoader label="Signing you in…" />;
  if (isBootstrapping || (!bootstrapped && !account)) {
    return <PageLoader label="Loading your library…" />;
  }
  if (bootstrapError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-or-cream-50 px-8 text-center">
        <h1 className="font-display text-2xl text-or-slate-900">We hit a snag</h1>
        <p className="text-[14px] text-or-slate-600">{bootstrapError}</p>
      </div>
    );
  }

  /**
   * Onboarding gate: a signed-in account with zero readers can't use any
   * reader-scoped feature (bookmarks, personalized shelf, Learn With Ollie).
   * Redirect them to the wizard unless they're already there — we can't just
   * render the Onboarding page inline because `Outlet` needs the child route
   * to mount its own component.
   */
  if (
    readers.length === 0 &&
    location.pathname !== '/onboarding' &&
    location.pathname !== '/pricing'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  /**
   * Mobile interstitial: the native apps aren't out yet, so on a phone/tablet
   * we show a one-time "Mobile Apps Coming Soon" screen with a "continue on
   * web" action instead of hard-blocking. Tapping continue persists the choice
   * (lib/mobileGate) and hands over the full web app. `/onboarding` and
   * `/pricing` skip the interstitial entirely so first-time signup and the
   * upgrade deep-link stay frictionless.
   */
  const mobilePlatform = getMobilePlatform();
  if (
    mobilePlatform &&
    !webContinue &&
    location.pathname !== '/onboarding' &&
    location.pathname !== '/pricing'
  ) {
    return (
      <MobileAppSplash
        onContinue={() => {
          setContinuedOnWeb();
          setWebContinue(true);
        }}
      />
    );
  }

  return <Outlet />;
}
